import { useEffect, useState } from 'react';
import { workspaceRequest } from './useWorkspaceResource';

// Saved assistant questions, shared across the account. Only the text is
// stored, so running one re-asks it against the page's current dates and data.
export default function useSavedAssistantPrompts({ accountId, userId, enabled = true }) {
  const [prompts, setPrompts] = useState([]), [notice, setNotice] = useState(null);
  useEffect(() => { setPrompts([]); setNotice(null); }, [accountId, userId]);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    workspaceRequest({ accountId, userId }, '/records/assistant_prompt').then(data => { if (active) setPrompts(Array.isArray(data?.items) ? data.items : []); })
      .catch(e => { if (active) setNotice(`Saved prompts could not be loaded: ${e.message}`); });
    return () => { active = false; };
  }, [enabled, accountId, userId]);
  async function save(question) {
    const text = question.trim(); if (!text) return;
    if (prompts.some(prompt => prompt.document.question === text)) { setNotice('This prompt is already saved.'); return; }
    const name = window.prompt('Name this saved prompt (shared with everyone on this client)', text.length > 60 ? `${text.slice(0, 57)}…` : text);
    if (name === null) return;
    try {
      const record = await workspaceRequest({ accountId, userId }, '/records/assistant_prompt', { method: 'POST', body: { name: name.trim() || text.slice(0, 60), document: { question: text } } });
      setPrompts(items => [record, ...items]); setNotice(null);
    } catch (e) { setNotice(`The prompt could not be saved: ${e.message}`); }
  }
  async function remove(record) {
    if (!window.confirm(`Remove the saved prompt “${record.name}” for everyone on this client?`)) return;
    try {
      await workspaceRequest({ accountId, userId }, `/records/assistant_prompt/${record.id}`, { method: 'DELETE', body: { revision: record.revision } });
      setPrompts(items => items.filter(item => item.id !== record.id)); setNotice(null);
    } catch (e) { setNotice(`The prompt could not be removed: ${e.message}`); }
  }
  return { prompts, notice, clearNotice: () => setNotice(null), save, remove };
}

// Save an already-asked question: the box clears after each answer, so the
// main Save prompt button cannot reach it.
export function SaveAskedQuestion({ saved, question }) {
  const text = (question || '').trim();
  if (!text) return null;
  if (saved.prompts.some(prompt => prompt.document.question === text)) return <span className="ow-assistant-saved-mark">Saved prompt</span>;
  return <button type="button" className="ow-text-button ow-assistant-save-asked" onClick={() => saved.save(text)}>Save prompt</button>;
}

export const SAVE_PROMPT_HINT = 'Type a question to save it, or use Save prompt under any question you have already asked.';

export function SavedAssistantPrompts({ saved, onRun, disabled }) {
  return <>
    {saved.prompts.length > 0 && <div className="ow-assistant-saved" aria-label="Saved prompts">
      <p className="ow-footnote">Saved prompts are shared with everyone on this client and run on the page’s current dates and data.</p>
      {saved.prompts.map(prompt => <span key={prompt.id} className="ow-assistant-saved-prompt">
        <button type="button" className="ow-button" title={prompt.document.question} onClick={() => onRun(prompt.document.question)} disabled={disabled}>{prompt.name}</button>
        <button type="button" className="ow-text-button" aria-label={`Remove saved prompt ${prompt.name}`} onClick={() => saved.remove(prompt)}>×</button>
      </span>)}
    </div>}
    {saved.notice && <p className="ow-assistant-apply-notice ow-assistant-apply-error" role="status">{saved.notice} <button type="button" className="ow-text-button" onClick={saved.clearNotice}>Dismiss</button></p>}
  </>;
}
